import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import WaitlistModal from "../components/WaitlistModal";
import EigooMark from "../components/EigooMark";
import { Skull, Lock, ArrowRight, ShieldOff, Crosshair, Eye, Bug, Fingerprint, Activity } from "lucide-react";

const ENGAGEMENTS = [
    { code: "RT-001", name: "Adversarial Red Team", duration: "14 days", scope: "Cloud + endpoint + identity", price: "from $48k", crew: "4-tier operators" },
    { code: "TH-006", name: "Active Threat Hunt", duration: "30 days", scope: "EDR/SIEM telemetry sweep", price: "from $32k", crew: "2 hunters + lead" },
    { code: "PT-014", name: "Application Pen Test", duration: "10 days", scope: "Web + API + auth flows", price: "from $24k", crew: "2 testers + reviewer" },
    { code: "PR-022", name: "Purple Team Exercise", duration: "21 days", scope: "Detection engineering co-op", price: "from $58k", crew: "Red + blue + analyst" },
    { code: "CR-031", name: "Crisis Tabletop (CISO-grade)", duration: "3 days", scope: "Board-ready incident scenario", price: "from $18k", crew: "Lead + board liaison" },
];

const SIGNALS = [
    { icon: Bug, label: "Zero-day disclosure pipeline", detail: "Private vulnerability surface, NDA-gated." },
    { icon: Crosshair, label: "Adversary emulation matrix", detail: "MITRE ATT&CK-mapped, tier-3 TTPs." },
    { icon: Fingerprint, label: "Identity attack chain validation", detail: "From phishing to domain dominance." },
    { icon: Eye, label: "OSINT exposure brief", detail: "What an attacker sees before you do." },
];

export default function CyberOpsSaHome() {
    const { user } = useAuth();
    const [showWaitlist, setShowWaitlist] = useState(false);
    return (
        <div className="min-h-screen bg-[hsl(151_55%_4%)] grain-overlay text-foreground" data-testid="cyberopssa-home">
            {showWaitlist && (
                <WaitlistModal tenant="cyberopssa" label="Request a Vault audit" onClose={() => setShowWaitlist(false)} />
            )}

            <header className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                <div className="flex items-baseline gap-3">
                    <EigooMark size="lg" sub="Tech Ops SA · The Vault" testid="cyberopssa-mark" />
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to="/?tenant=cyberops"
                        data-testid="cyberopssa-exit-vault"
                        className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                    >
                        ← Exit to general Tech Ops
                    </Link>
                    {user ? (
                        <Link to="/app/dashboard" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="cyberopssa-portal">Enter Portal</Link>
                    ) : (
                        <>
                            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Sign In</Link>
                            <button
                                onClick={() => setShowWaitlist(true)}
                                data-testid="cyberopssa-cta"
                                className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                            >
                                Request Clearance
                            </button>
                        </>
                    )}
                </div>
            </header>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-24">
                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(350_60%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(350_80%_72%)] mb-6">
                        <Skull size={10} className="text-[hsl(350_80%_72%)]" /> Vetted operators only · NDA-gated
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                        The Vault.
                        <span className="block gold-gradient-text mt-2 italic font-normal">Where the elite audits live.</span>
                    </h1>
                    <p
                        className="mt-8 text-lg text-foreground/80 max-w-xl leading-relaxed"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                    >
                        Tech Ops SA is the dark-room version of Tech Ops — adversarial red teams, threat hunters,
                        and crisis-grade purple ops. Engagements are scoped under NDA, crewed by tier-3+ operators,
                        and never appear on the public docket.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="glass rounded-2xl p-6 lg:col-span-1 border border-[hsl(350_60%_30%/0.25)]">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(350_80%_72%)]">Vault posture</div>
                        <div className="font-heading text-6xl mt-3 gold-gradient-text">CLASSIFIED</div>
                        <div className="text-xs text-muted-foreground mt-2">Engagement details visible only after NDA &amp; vetting.</div>
                        <div className="mt-6 space-y-3 text-xs">
                            {SIGNALS.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <div key={s.label} className="flex items-start gap-3">
                                        <Icon size={14} className="text-[hsl(45_85%_68%)] mt-0.5 shrink-0" />
                                        <div>
                                            <div className="text-foreground/90">{s.label}</div>
                                            <div className="text-muted-foreground text-[10px] uppercase tracking-wider mt-0.5">{s.detail}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-6 flex items-center gap-2 text-xs text-[hsl(350_80%_72%)]">
                            <Lock size={14} /> SOC2 Type II · OSCP · OSEP · GREM
                        </div>
                    </div>

                    <div className="glass rounded-2xl p-6 lg:col-span-2 border border-[hsl(350_60%_30%/0.25)]">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="font-heading text-2xl">Vault docket</div>
                                <div className="text-xs text-muted-foreground">Active engagement classes <span className="text-[hsl(350_80%_72%)]">(sample roster)</span></div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[hsl(350_80%_72%)]">
                                <span className="w-2 h-2 rounded-full bg-[hsl(350_80%_72%)] animate-pulse" />
                                operators on watch
                            </div>
                        </div>
                        <div className="space-y-2">
                            {ENGAGEMENTS.map((e) => (
                                <div key={e.code} className="p-3 rounded-lg bg-[hsl(151_55%_8%)] border border-[hsl(350_50%_25%/0.2)] flex items-center gap-4" data-testid={`vault-engagement-${e.code}`}>
                                    <div className="font-mono text-xs text-[hsl(350_80%_72%)] w-14">{e.code}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{e.name}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{e.scope}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground w-20 text-right">{e.duration}</div>
                                    <div className="text-xs text-foreground/85 w-24 text-right">{e.crew}</div>
                                    <div className="font-mono text-xs gold-gradient-text w-20 text-right">{e.price}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 p-4 rounded-lg border border-dashed border-[hsl(350_60%_40%/0.3)] text-xs text-muted-foreground">
                            <span className="text-[hsl(350_80%_72%)] font-semibold">Vault clearance required.</span> Submit through the Sovereign Match scouter on the main hub or request a private brief above.
                        </div>
                    </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                    <button
                        onClick={() => setShowWaitlist(true)}
                        className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        data-testid="vault-cta"
                    >
                        <ShieldOff size={18} /> Submit Engagement Brief <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    <Link
                        to="/?tenant=cyberops"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[hsl(40_50%_35%/0.4)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors"
                    >
                        <Activity size={16} /> Need general IT instead? → Tech Ops
                    </Link>
                </div>
            </section>

            <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(350_60%_30%/0.18)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Tech Ops SA · The Vault is sealed by default.</div>
                <div className="font-mono">techopssa.eigooinc.net</div>
            </footer>
        </div>
    );
}
